/* Flow (MAR-372) — the frame for a short linear flow rendered outside the app shell
   (onboarding, the pre-auth connect page, first account setup), from web-app/Onboarding.html.
   Appended to the library through `export * from './Flow'` in ./index.jsx. */
import * as React from 'react';
import { Orb } from './index.jsx';

const cx = (...a) => a.filter(Boolean).join(' ');

/** A standalone screen: `.mv-root` base, the Marva mark on top, a centred column that scrolls. */
function FlowScreen({ aside, children, className = '', ...rest }) {
  return (
    <div className="mv-root">
      <div {...rest} className={cx('mv-flow', className)}>
        <header className="mv-flow-top">
          <Orb size={28} />
          <span className="mv-flow-brand">Marva</span>
          <span className="mv-spring" />
          {aside}
        </header>
        <main className="mv-flow-stage">{children}</main>
      </div>
    </div>
  );
}

/** The centred head of a step: an icon tile (or the Orb), a serif title, a lede. */
function FlowIntro({ icon, orb = false, badge, title, titleAs: T = 'h1', children, className = '' }) {
  return (
    <div className={cx('mv-flow-intro', className)}>
      {orb ? <Orb size={58} float /> : icon ? <span className="mv-flow-ic" aria-hidden="true">{icon}</span> : null}
      {badge}
      {title && <T className="mv-flow-title">{title}</T>}
      {children && <p className="mv-flow-lede">{children}</p>}
    </div>
  );
}

/** What a step promises, line by line. `check`: a sage tick list (permissions). */
function FlowList({ items = [], variant = 'icon', label, className = '' }) {
  return (
    <ul className={cx('mv-flow-list', variant === 'check' && 'mv-flow-list--check', className)} aria-label={label}>
      {items.map((it, i) => (
        <li key={it.key || i} className="mv-flow-li">
          <span className="mv-flow-li-ic" aria-hidden="true">{it.icon}</span>
          <div className="mv-flow-li-text">
            <div className="mv-flow-li-title">{it.title}</div>
            {it.description && <div className="mv-flow-li-desc">{it.description}</div>}
          </div>
        </li>
      ))}
    </ul>
  );
}

/** "or" between two ways through a step. */
function FlowDivider({ children = 'or' }) {
  return <div className="mv-flow-or">{children}</div>;
}

/** Small print under a flow: terms, a trust line, a help line. */
function FlowFine({ children, className = '', ...rest }) {
  return <p {...rest} className={cx('mv-flow-fine', className)}>{children}</p>;
}

/** The help line in the flow's top bar ("Need a hand?"). */
function FlowHelp({ children, className = '' }) {
  return <span className={cx('mv-flow-help', className)}>{children}</span>;
}

export { FlowScreen, FlowIntro, FlowList, FlowDivider, FlowFine, FlowHelp };
